import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { CheckIcon, XCircle, ChevronDown, XIcon } from 'lucide-react';
import { cn } from '@/shared/lib/utils';
import { Separator } from '@/ui/separator';
import { Button } from '@/ui/button';
import { Badge } from '@/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '@/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/ui/command';

const multiSelectVariants = cva(
  'shrink-0 transition-colors text-xs font-medium border',
  {
    variants: {
      variant: {
        default: 'border-foreground/10 text-foreground bg-card hover:bg-card/80',
        secondary: 'border-foreground/10 bg-secondary text-secondary-foreground hover:bg-secondary/80',
        destructive: 'border-transparent bg-destructive text-destructive-foreground hover:bg-destructive/80',
        inverted: 'inverted',
      },
    },
    defaultVariants: { variant: 'default' },
  }
);

export interface MultiSelectOption {
  label: string;
  value: string;
  icon?: React.ComponentType<{ className?: string }>;
  disabled?: boolean;
}

interface MultiSelectProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'defaultValue'>,
  VariantProps<typeof multiSelectVariants> {
  options: MultiSelectOption[];
  onValueChange: (value: string[]) => void;
  defaultValue?: string[];
  value?: string[];
  placeholder?: string;
  modalPopover?: boolean;
  className?: string;
  disabled?: boolean;
  searchable?: boolean;
  hideSelectAll?: boolean;
}

export const MultiSelect = React.forwardRef<HTMLButtonElement, MultiSelectProps>(
  (
    {
      options,
      onValueChange,
      variant,
      defaultValue = [],
      value,
      placeholder = 'Select options',
      modalPopover = false,
      className,
      disabled = false,
      searchable = true,
      hideSelectAll = false,
      ...props
    },
    ref
  ) => {
    const isControlled = value !== undefined;
    const [internalSelected, setInternalSelected] = React.useState<string[]>(defaultValue);
    const selectedValues = isControlled ? value! : internalSelected;

    const [isPopoverOpen, setIsPopoverOpen] = React.useState(false);
    const [searchValue, setSearchValue] = React.useState('');
    const [visibleCount, setVisibleCount] = React.useState<number>(selectedValues.length);

    const badgesRowRef = React.useRef<HTMLDivElement>(null);
    const measureRef = React.useRef<HTMLDivElement>(null);

    React.useEffect(() => {
      if (!isControlled) setInternalSelected(defaultValue);
    }, [JSON.stringify(defaultValue)]);

    React.useEffect(() => {
      if (!isPopoverOpen) setSearchValue('');
    }, [isPopoverOpen]);

    React.useLayoutEffect(() => {
      const row = badgesRowRef.current;
      const measure = measureRef.current;
      if (!row || !measure || selectedValues.length === 0) {
        setVisibleCount(selectedValues.length);
        return;
      }

      const compute = () => {
        const rowWidth = row.offsetWidth;
        const overflowBadgeWidth = 64;
        let used = 0;
        let count = 0;

        // Read widths only from the hidden measurement layer, not the visible badges.
        const children = Array.from(measure.querySelectorAll<HTMLElement>('[data-badge]'));
        for (let i = 0; i < children.length; i++) {
          const child = children[i];
          const w = child.offsetWidth + 4; // 4px gap
          const isLast = i === selectedValues.length - 1;
          // If this is the last item it doesn't need room for "+N more".
          const needed = isLast ? used + w : used + w + overflowBadgeWidth;
          if (needed > rowWidth) break;
          used += w;
          count++;
        }

        setVisibleCount(Math.max(1, count));
      };

      compute();
      const ro = new ResizeObserver(compute);
      ro.observe(row);
      return () => ro.disconnect();
    }, [selectedValues]);

    const filteredOptions = searchable && searchValue
      ? options.filter(o =>
        o.label.toLowerCase().includes(searchValue.toLowerCase()) ||
        o.value.toLowerCase().includes(searchValue.toLowerCase())
      )
      : options;

    const toggleOption = (optionValue: string) => {
      if (disabled) return;
      const next = selectedValues.includes(optionValue)
        ? selectedValues.filter(v => v !== optionValue)
        : [...selectedValues, optionValue];
      if (!isControlled) setInternalSelected(next);
      onValueChange(next);
    };

    const handleClear = () => {
      if (disabled) return;
      if (!isControlled) setInternalSelected([]);
      onValueChange([]);
    };

    const toggleAll = () => {
      if (disabled) return;
      const enabledOptions = options.filter(o => !o.disabled);
      const next = selectedValues.length === enabledOptions.length
        ? []
        : enabledOptions.map(o => o.value);
      if (!isControlled) setInternalSelected(next);
      onValueChange(next);
    };

    const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') setIsPopoverOpen(true);
      else if (e.key === 'Backspace' && !e.currentTarget.value) {
        const next = selectedValues.slice(0, -1);
        if (!isControlled) setInternalSelected(next);
        onValueChange(next);
      }
    };

    const overflowCount = selectedValues.length - visibleCount;

    return (
      <Popover open={isPopoverOpen} onOpenChange={setIsPopoverOpen} modal={modalPopover}>
        <PopoverTrigger asChild>
          <Button
            ref={ref}
            {...props}
            onClick={() => !disabled && setIsPopoverOpen(prev => !prev)}
            disabled={disabled}
            className={cn(
              'h-10 flex px-2 rounded-md border items-center justify-between bg-background hover:bg-background w-full [&_svg]:pointer-events-auto font-normal overflow-hidden',
              className
            )}
          >
            {selectedValues.length > 0 ? (
              <div className="flex items-center justify-between w-full min-w-0 gap-1">
                <div
                  ref={badgesRowRef}
                  className="flex items-center gap-1 flex-nowrap overflow-hidden flex-1 min-w-0 relative"
                >
                  {/* Invisible measurement layer — all badges rendered but hidden, constrained to row width */}
                  <div
                    ref={measureRef}
                    className="absolute inset-0 opacity-0 pointer-events-none flex gap-1 flex-nowrap overflow-hidden"
                    aria-hidden
                  >
                    {selectedValues.map(val => {
                      const option = options.find(o => o.value === val);
                      if (!option) return null;
                      return (
                        <span
                          key={val}
                          data-badge
                          className={cn(multiSelectVariants({ variant }), 'inline-flex items-center px-2 py-0.5 rounded-full whitespace-nowrap')}
                        >
                          {option.label}
                        </span>
                      );
                    })}
                  </div>

                  {selectedValues.slice(0, visibleCount).map(val => {
                    const option = options.find(o => o.value === val);
                    if (!option) return null;
                    return (
                      <Badge
                        key={val}
                        data-badge
                        className={cn(
                          multiSelectVariants({ variant }),
                          'inline-flex items-center gap-1 px-2 py-0.5 rounded-full whitespace-nowrap shrink-0 [&>svg]:pointer-events-auto'
                        )}
                      >
                        {option.icon && <option.icon className="h-3 w-3" />}
                        <span>{option.label}</span>
                        <div
                          role="button"
                          tabIndex={0}
                          onClick={e => { e.stopPropagation(); toggleOption(val); }}
                          onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); toggleOption(val); } }}
                          className="cursor-pointer rounded-sm hover:bg-black/10 p-0.5 -mr-0.5"
                        >
                          <XCircle className="h-3 w-3" />
                        </div>
                      </Badge>
                    );
                  })}

                  {overflowCount > 0 && (
                    <Badge className={cn(multiSelectVariants({ variant }), 'px-2 py-0.5 rounded-full whitespace-nowrap shrink-0')}>
                      +{overflowCount} more
                    </Badge>
                  )}
                </div>

                <div className="flex items-center shrink-0 ml-1">
                  <span
                    role="button"
                    tabIndex={disabled ? -1 : 0}
                    aria-label="Clear selected options"
                    aria-disabled={disabled}
                    onClick={e => {
                      e.stopPropagation();
                      if (!disabled) handleClear();
                    }}
                    onKeyDown={e => {
                      if ((e.key === 'Enter' || e.key === ' ') && !disabled) {
                        e.preventDefault();
                        e.stopPropagation();
                        handleClear();
                      }
                    }}
                    className="p-1 cursor-pointer text-muted-foreground hover:text-foreground"
                  >
                    <XIcon className="h-3.5 w-3.5" />
                  </span>
                  <Separator orientation="vertical" className="h-5" />
                  <ChevronDown className="h-3.5 w-3.5 mx-1.5 text-muted-foreground" />
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-between w-full">
                <span className="text-sm text-muted-foreground px-1">{placeholder}</span>
                <ChevronDown className="h-3.5 w-3.5 mx-1.5 text-muted-foreground shrink-0" />
              </div>
            )}
          </Button>
        </PopoverTrigger>

        <PopoverContent
          className="w-[var(--radix-popover-trigger-width)] p-0"
          align="start"
          onEscapeKeyDown={() => setIsPopoverOpen(false)}
        >
          <Command>
            {searchable && (
              <CommandInput
                placeholder="Search..."
                onKeyDown={handleInputKeyDown}
                value={searchValue}
                onValueChange={setSearchValue}
              />
            )}
            <CommandList className="[scrollbar-width:thin] [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar]:h-1.5 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-border [&::-webkit-scrollbar-thumb]:hover:bg-border/80">
              <CommandEmpty>No results found.</CommandEmpty>
              {!hideSelectAll && !searchValue && (
                <CommandGroup>
                  <CommandItem onSelect={toggleAll} className="cursor-pointer">
                    <div className={cn(
                      'mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-primary',
                      selectedValues.length === options.filter(o => !o.disabled).length
                        ? 'bg-primary text-primary-foreground'
                        : 'opacity-50 [&_svg]:invisible'
                    )}>
                      <CheckIcon className="h-4 w-4" />
                    </div>
                    <span>Select All</span>
                  </CommandItem>
                </CommandGroup>
              )}
              <CommandGroup>
                {filteredOptions.map(option => {
                  const isSelected = selectedValues.includes(option.value);
                  return (
                    <CommandItem
                      key={option.value}
                      onSelect={() => toggleOption(option.value)}
                      disabled={option.disabled}
                      className={cn('cursor-pointer', option.disabled && 'opacity-50 cursor-not-allowed')}
                    >
                      <div className={cn(
                        'mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-primary',
                        isSelected ? 'bg-primary text-primary-foreground' : 'opacity-50 [&_svg]:invisible'
                      )}>
                        <CheckIcon className="h-4 w-4" />
                      </div>
                      {option.icon && <option.icon className="mr-2 h-4 w-4 text-muted-foreground" />}
                      <span>{option.label}</span>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
              <CommandSeparator />
              <CommandGroup>
                <div className="flex items-center justify-between">
                  {selectedValues.length > 0 && (
                    <>
                      <CommandItem onSelect={handleClear} className="flex-1 justify-center cursor-pointer">
                        Clear
                      </CommandItem>
                      <Separator orientation="vertical" className="min-h-6" />
                    </>
                  )}
                  <CommandItem onSelect={() => setIsPopoverOpen(false)} className="flex-1 justify-center cursor-pointer">
                    Close
                  </CommandItem>
                </div>
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    );
  }
);

MultiSelect.displayName = 'MultiSelect';
export type { MultiSelectProps };
